/**
 * The Emails area's reads (spec 2026-10-16-emails-marketing F28). The URLs live here, where
 * `api-requests.test.ts` executes them against the Worker catalog, never inside a `useQuery` closure.
 * Each read normalises its answer in `select`, so a renamed or missing field blanks one cell rather than
 * breaking a screen (`error-paths.test.ts` renders every page against `{}`).
 */
import { apiGet } from "@/lib/api";
import { AGGREGATE, DETAIL, LIVE_LIST } from "@/lib/query-freshness";
import type {
  ContactDetail,
  ContactList,
  EmailBrand,
  EmailBrandVersion,
  Segment,
  SegmentFields,
} from "@/types-email";

export const CONTACTS_PAGE_SIZE = 25;

/** The template the Brand page previews: a short, ordinary transactional email. */
export const BRAND_PREVIEW_TEMPLATE = "order.created";

const EMPTY_BRAND: EmailBrand = {
  headerImage: { url: "", width: 0, height: 0, alt: "" },
  headerHref: "",
  footerReason: "",
  legalLine: "",
  address: "",
  supportEmail: "",
  social: {},
  privacyUrl: "",
  termsUrl: "",
  version: 0,
};

function normalizeBrand(data: Partial<EmailBrand> | undefined): EmailBrand {
  return {
    ...EMPTY_BRAND,
    ...data,
    headerImage: { ...EMPTY_BRAND.headerImage, ...data?.headerImage },
    social: { ...data?.social },
    version: typeof data?.version === "number" ? data.version : 0,
  };
}

export function emailBrandQuery() {
  return {
    ...DETAIL,
    queryKey: ["email", "brand"] as const,
    select: normalizeBrand,
    queryFn: () => apiGet<EmailBrand>("/api/admin/email/brand"),
  };
}

export function emailBrandVersionsQuery() {
  return {
    ...DETAIL,
    queryKey: ["email", "brand", "versions"] as const,
    select: (data: EmailBrandVersion[] | undefined) =>
      Array.isArray(data) ? data : [],
    queryFn: () =>
      apiGet<EmailBrandVersion[]>("/api/admin/email/brand/versions"),
  };
}

export interface EmailContactsParams {
  kind: string;
  status: string;
  q: string;
  page: number;
}

function normalizeContacts(
  data: Partial<ContactList> | undefined,
): ContactList {
  return {
    rows: (data?.rows ?? []).map((row) => ({
      ...row,
      topics: {
        offers: row.topics?.offers ?? false,
        newsletter: row.topics?.newsletter ?? false,
      },
      companies: row.companies ?? 0,
    })),
    total: data?.total ?? 0,
    page: data?.page ?? 1,
    pageSize: data?.pageSize ?? CONTACTS_PAGE_SIZE,
  };
}

export function emailContactsQuery(params: EmailContactsParams) {
  return {
    ...LIVE_LIST,
    queryKey: [
      "email",
      "contacts",
      params.kind,
      params.status,
      params.q,
      params.page,
    ] as const,
    select: normalizeContacts,
    queryFn: () =>
      apiGet<ContactList>("/api/admin/email/contacts", {
        kind: params.kind || undefined,
        status: params.status || undefined,
        q: params.q || undefined,
        page: params.page,
        pageSize: CONTACTS_PAGE_SIZE,
      }),
  };
}

function normalizeContact(
  data: Partial<ContactDetail> | undefined,
): ContactDetail {
  return {
    email: "",
    kind: null,
    firstName: null,
    lastName: null,
    source: null,
    status: "marketable",
    suppressed: false,
    suppressedReason: null,
    staffOptOut: null,
    support: null,
    refreshedAt: null,
    createdAt: null,
    ...data,
    topics: {
      offers: data?.topics?.offers ?? false,
      newsletter: data?.topics?.newsletter ?? false,
    },
    companies: data?.companies ?? [],
    events: data?.events ?? [],
  };
}

export function emailContactQuery(email: string) {
  return {
    ...DETAIL,
    queryKey: ["email", "contact", email] as const,
    select: normalizeContact,
    queryFn: () =>
      apiGet<ContactDetail>(
        `/api/admin/email/contacts/${encodeURIComponent(email)}`,
      ),
  };
}

export function emailSegmentsQuery(status: "active" | "archived" | "all") {
  return {
    ...LIVE_LIST,
    queryKey: ["email", "segments", status] as const,
    select: (data: Segment[] | undefined) => (Array.isArray(data) ? data : []),
    queryFn: () =>
      apiGet<Segment[]>("/api/admin/email/segments", {
        status: status === "active" ? undefined : status,
      }),
  };
}

function normalizeFields(
  data: Partial<SegmentFields> | undefined,
): SegmentFields {
  return {
    fields: (data?.fields ?? []).map((field) => ({
      ...field,
      operators: field.operators ?? [],
      suggestions: field.suggestions ?? [],
    })),
    topics: data?.topics ?? ["offers", "newsletter"],
    maxConditions: data?.maxConditions ?? 20,
    maxDepth: data?.maxDepth ?? 2,
  };
}

/** The field registry is code upstream plus value suggestions from the facts: an aggregate read. */
export function emailSegmentFieldsQuery() {
  return {
    ...AGGREGATE,
    queryKey: ["email", "segment-fields"] as const,
    select: normalizeFields,
    queryFn: () => apiGet<SegmentFields>("/api/admin/email/segments/fields"),
  };
}

export function emailSegmentQuery(segmentId: string) {
  return {
    ...DETAIL,
    queryKey: ["email", "segment", segmentId] as const,
    select: (data: Partial<Segment> | undefined) =>
      ({
        id: segmentId,
        name: "",
        description: null,
        rules: null,
        status: "active",
        lastRunCount: null,
        lastRunAt: null,
        ...data,
      }) as Segment,
    queryFn: () =>
      apiGet<Segment>(
        `/api/admin/email/segments/${encodeURIComponent(segmentId)}`,
      ),
  };
}
